import type { NavigateFunction } from "react-router-dom";
import type { User } from "../@types";

/** Core steps plus frontend-only contact-phone and residence checkpoints. */
export type KycNextStep =
  | "verify_email"
  | "verify_contact_phone"
  | "confirm_residence"
  | "verify_nin"
  | "submit_national_identity"
  /** Legacy Core hint kept only for a safe rolling deployment. */
  | "verify_address"
  | "complete_profile"
  | "verify_phone"
  | "set_password"
  | "verify_bvn"
  | "employment"
  | "kyc_review"
  | "complete";

const isTruthy = (v: unknown): boolean => {
  if (v === true) return true;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v.length > 0 && v !== "0";
  return !!v;
};

const NIGERIA = "NG";

/**
 * Whether this customer lives outside Nigeria, and so gets the applicable
 * country-specific identity-number branch instead of the NIN branch.
 *
 * Mirrors Core's `isInternationalResident` and Laravel's `ResidencyResolver`,
 * including the property all three depend on: only a POSITIVE non-Nigerian
 * signal counts. An unknown residence reads as domestic.
 *
 * That default is what makes this safe to ship. Every existing user has no
 * `residence_country`, and this function runs on every navigation, so
 * defaulting the other way would push every current customer into an address
 * gate at once.
 *
 * Kept in sync with Core deliberately rather than deferring to the API's
 * `nextStep`: this same funnel has to work while offline-ish (a cached user, a
 * failed status call), and a client that could not decide would strand people.
 */
export function isInternationalResident(user: User | null | undefined): boolean {
  if (!user) return false;
  const declared = user.residence_country?.trim().toUpperCase();
  return Boolean(declared && declared !== NIGERIA);
}

/**
 * Whether a structured home address is on file for dashboard and service KYC.
 *
 * Presence only, never postcode rules. Laravel refuses to save an address
 * missing a postcode where the country requires one, so the check has already
 * run by the time these fields are populated. The legacy `verified_address`
 * flag is not consulted — it was set by a verifyAddress() that returned
 * success before ever calling its provider.
 */
export function hasStructuredAddress(user: User | null | undefined): boolean {
  const c = user?.compliance;
  if (!c) return false;
  return Boolean(c.address_line_1?.trim() && c.address_city?.trim() &&
    c.address_country?.trim().toUpperCase() === user?.residence_country?.trim().toUpperCase());
}

export function hasNationalIdentitySubmitted(user: User | null | undefined): boolean {
  return Boolean(user?.compliance?.national_identity_number_last_four);
}

/** Name/DOB profile step is done (independent of password — password is terminal). */
const profileComplete = (user: User): boolean =>
  isTruthy(user.firstname) &&
  isTruthy(user.lastname) &&
  isTruthy(user.date_of_birth);

/**
 * Client-side funnel, in the product-mandated order (password LAST):
 * email → contact-phone → residence → country identity → profile (name/DOB)
 * → identity-phone → set-password → complete.
 *
 * Password is deliberately the final data step; it no longer gates the earlier
 * screens. The welcome screen after password is imperative, not a funnel step —
 * a resumed user who already set a password resolves to `complete` and lands on
 * `/home`. Username and profile photo are optional dashboard setup tasks.
 *
 * NIN is the only identity hard-blocker at signup. BVN was moved to a
 * post-signup dashboard task, so it must NOT gate here. Employment and KYC
 * review are dashboard tasks, not reasons to trap users outside `/home`.
 *
 * The funnel guides *new* users but must never trap a returning one: NIN (and
 * the earlier gates) only fire while a value is genuinely still missing, so an
 * existing/active user is never looped back into the signup screens.
 */
export function inferNextStepFromUser(user: User | null): KycNextStep | null {
  if (!user) return null;
  if (!user.email_verified) return "verify_email";

  const c = user.compliance;
  const international = isInternationalResident(user);

  // New international customers submit their country-specific identity number
  // before profile completion. Verification may remain pending, but the number
  // must be present. Password-set customers are deliberately grandfathered so
  // existing users are never pulled back into signup.
  const needsIdentity = international
    ? !isTruthy(user.password_set) && !hasNationalIdentitySubmitted(user)
    : !c || (!isTruthy(c.nin_verified) && !c.nin);

  // Support contact phone is collected + verified BEFORE the identity step. Gate
  // it only while the user is still moving through that funnel, so a returning
  // or existing user — who predates this field — is never trapped here.
  if (needsIdentity && !isTruthy(user.contact_phone_verified)) {
    return "verify_contact_phone";
  }
  if (needsIdentity && !user.residence_country) return "confirm_residence";
  if (needsIdentity) return international ? "submit_national_identity" : "verify_nin";

  // Post-identity: profile (name/DOB) → identity-phone OTP → password.
  if (!profileComplete(user)) return "complete_profile";
  // Only a signup step. Once the password is set, an unverified phone (say,
  // one just added in Settings) is the dashboard's "Verify Phone Number"
  // task; gating it here would bounce the customer into the signup screen
  // mid-task and drop where they came from.
  if (user.phone && !user.phone_verified && !isTruthy(user.password_set)) {
    return "verify_phone";
  }
  if (!user.password_set) return "set_password";
  return "complete";
}

/**
 * First route to show for this step (Core `nextStep` or inferred).
 * Returns `null` when the user should land on the dashboard (`complete` or unknown).
 */
export function pathForNextStep(
  step: KycNextStep | string | null | undefined,
): string | null {
  if (
    !step ||
    step === "complete" ||
    step === "employment" ||
    step === "kyc_review"
  ) {
    return null;
  }
  const map: Record<string, string> = {
    verify_email: "/sign-up",
    verify_contact_phone: "/sign-up/contact-phone",
    confirm_residence: "/sign-up/confirm-residence",
    verify_nin: "/sign-up/verify-nin",
    submit_national_identity: "/sign-up/national-identity",
    complete_profile: "/sign-up/complete-profile",
    verify_phone: "/sign-up/verify-phone-number",
    set_password: "/sign-up/create-password",
    verify_bvn: "/sign-up/bvn-verification",
    employment: "/home/settings/compliance/employment-details",
    kyc_review: "/home/settings/compliance/kyc",
  };
  return map[step] ?? null;
}

export function isOnboardingComplete(user: User | null): boolean {
  return inferNextStepFromUser(user) === "complete";
}

/** True when the current URL is the (or a) screen for this onboarding step. */
export function isPathForCurrentOnboardingStep(
  step: KycNextStep | null,
  pathname: string,
): boolean {
  if (!step || step === "complete") return true;
  const base = pathname.split("?")[0];

  /**
   * `kyc_review` maps to the KYC tiers screen, but users must be able to open the
   * whole Compliance area (hub, utility bill, etc.) and the main app without being
   * forced back to `/home/settings/compliance/kyc` on every navigation.
   */
  if (step === "kyc_review") {
    if (base === "/home" || base.startsWith("/home/")) return true;
    if (base.startsWith("/home/settings/compliance")) return true;
  }

  const p = pathForNextStep(step);
  if (!p) return true;
  return base === p || base.startsWith(`${p}/`);
}

/** Prefer API `nextStep` when present; otherwise infer from profile user. */
export function resolveNextStep(
  apiNextStep: string | null | undefined,
  user: User | null,
): KycNextStep | null {
  const inferred = inferNextStepFromUser(user);
  // A profile refreshed after verification can be newer than the API route hint.
  if (apiNextStep === "verify_nin" && isTruthy(user?.compliance?.nin_verified)) {
    return inferred;
  }
  // Support contact phone is a frontend/Laravel-only gate that Core knows nothing
  // about, so it can never come back as `apiNextStep`. Enforce it ahead of any
  // Core-emitted step whenever the funnel still needs it.
  if (inferred === "verify_contact_phone") return "verify_contact_phone";
  if (inferred === "confirm_residence") return "confirm_residence";
  // Ignore two Core-emitted steps and infer instead:
  //  - `verify_bvn`: BVN is now a post-signup dashboard task, not a hard-blocker.
  //  - `complete_profile`: its meaning changed here (was "set password", now the
  //    name/DOB screen). During/after the password-last rollout Core's emission is
  //    ambiguous, and the inferred funnel is authoritative. Core's `set_password`
  //    (the new terminal step) is still honoured.
  // Core and this file compute the residency branch the same way, but Core reads
  // `residence_country` from the database while this reads it off the cached
  // user. During the rollout a client can hold a user object older than the
  // column, so an inferred international branch wins over a Core-emitted
  // `verify_nin` — sending an international customer to a NIN screen is the one
  // failure this whole change exists to prevent.
  if (
    inferred === "submit_national_identity" &&
    (apiNextStep === "verify_nin" || apiNextStep === "verify_address")
  ) {
    return "submit_national_identity";
  }
  if (
    apiNextStep &&
    isKycNextStep(apiNextStep) &&
    apiNextStep !== "verify_bvn" &&
    apiNextStep !== "complete_profile" &&
    apiNextStep !== "verify_address"
  ) {
    return apiNextStep;
  }
  return inferred;
}

function isKycNextStep(s: string): s is KycNextStep {
  return [
    "verify_email",
    "verify_contact_phone",
    "confirm_residence",
    "verify_nin",
    "submit_national_identity",
    "verify_address",
    "complete_profile",
    "verify_phone",
    "set_password",
    "verify_bvn",
    "employment",
    "kyc_review",
    "complete",
  ].includes(s);
}

/** After storing JWT and user, send the user to the first incomplete onboarding screen or home. */
export function navigateForOnboardingStep(
  navigate: NavigateFunction,
  nextStep: string | undefined | null,
  user: User | null,
) {
  const step = resolveNextStep(nextStep, user);
  const path = pathForNextStep(step);
  if (path) navigate(path, { replace: true });
  else navigate("/home", { replace: true });
}
